import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, FormProvider } from 'react-hook-form';
import { FormTextField, SaveIndicator, SaveStatus } from '@personal-dashboard/ui';
import { LocalField, LocalSection } from './customFieldsQueries';
import { CustomFieldCard } from './CustomFieldCard';

interface CustomFieldSectionCardProps {
  section: LocalSection;
  onUpdateSectionTitle: (sectionId: string, title: string) => void;
  onAddField: (sectionId: string) => void;
  onSaveSection: (section: LocalSection) => void;
  onDeleteSection: (id: string) => void;
  onUpdateField: (sectionId: string, fieldId: string, updates: Partial<LocalField>) => void;
  onDeleteField: (sectionId: string, fieldId: string) => void;
  onAddOption: (sectionId: string, fieldId: string, optionValue: string) => void;
  onRemoveOption: (sectionId: string, fieldId: string, optionToRemove: string) => void;
  onTogglePublish: (sectionId: string, fieldId: string, published: boolean) => void;
  saveStatus?: SaveStatus;
}

export const CustomFieldSectionCard: React.FC<CustomFieldSectionCardProps> = ({
  section,
  onUpdateSectionTitle,
  onAddField,
  onSaveSection,
  onDeleteSection,
  onUpdateField,
  onDeleteField,
  onAddOption,
  onRemoveOption,
  onTogglePublish,
  saveStatus = 'idle',
}) => {
  const titleMethods = useForm<{ title: string }>({ defaultValues: { title: section.title } });
  const watchedTitle = titleMethods.watch('title');

  useEffect(() => {
    if (watchedTitle !== section.title) {
      onUpdateSectionTitle(section.id, watchedTitle);
    }
  }, [watchedTitle, section.id, section.title, onUpdateSectionTitle]);

  useEffect(() => {
    const current = titleMethods.getValues('title');
    if (current !== section.title) {
      titleMethods.reset({ title: section.title });
    }
  }, [section.title, titleMethods]);

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }} variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <FormProvider {...titleMethods}>
          <FormTextField
            name="title"
            variant="standard"
            InputProps={{ style: { fontSize: '1.25rem', fontWeight: 'bold' } }}
            sx={{ maxWidth: 300 }}
          />
        </FormProvider>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddField(section.id)}
          >
            Add Field
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SaveIcon />}
            onClick={() => onSaveSection(section)}
          >
            Save Section
          </Button>
          <SaveIndicator status={saveStatus} />
          <IconButton onClick={() => onDeleteSection(section.id)} color="error" size="small">
            <DeleteIcon />
          </IconButton>
        </Stack>
      </Box>

      {section.fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', my: 2 }}>
          No fields added yet. Click "Add Field" to start customizing this section.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {section.fields.map((field) => (
            <CustomFieldCard
              key={field.id}
              sectionId={section.id}
              field={field}
              onUpdateField={onUpdateField}
              onDeleteField={onDeleteField}
              onAddOption={onAddOption}
              onRemoveOption={onRemoveOption}
              onTogglePublish={onTogglePublish}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
};
